(function () {
  'use strict';

  if (customElements.get('product-sticky-add')) return;

  class ProductStickyAdd extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.abort = new AbortController();
      this.scheduleSync = this.scheduleSync.bind(this);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), {once: true, signal: this.abort.signal});
      } else {
        this.initialize();
      }
    }

    initialize() {
      const containers = document.querySelectorAll('#MainContent [data-section-type="product"]');
      this.productContainer = Array.from(containers).find(node =>
        node.dataset.productId === this.dataset.productId && node.dataset.modal !== 'true'
      );
      this.form = this.productContainer?.querySelector('form.product-single__form');
      this.originalButton = this.form?.querySelector('[data-add-to-cart]');
      if (!this.originalButton) return;

      this.exposureTracked = this.exposureTracked || false;
      this.pendingAdd = false;

      const variants = JSON.parse(this.querySelector('[data-sticky-variants]').textContent);
      this.variants = new Map(variants.map(variant => [String(variant.id), variant]));
      this.variantSelect = this.form.elements.namedItem('id');
      this.lastSelectValue = this.variantSelect?.value;
      const initialVariant = this.variants.get(this.dataset.initialVariantId);
      this.variant = this.originalButton.disabled && initialVariant && !initialVariant.available ? initialVariant :
        this.resolveVariant(this.lastSelectValue) || initialVariant;
      this.originalQuantity = this.form.elements.namedItem('quantity');
      if (!this.originalQuantity) {
        this.originalQuantity = document.createElement('input');
        Object.assign(this.originalQuantity, {type: 'hidden', name: 'quantity', value: '1'});
        this.form.append(this.originalQuantity);
      }

      this.quantity = this.querySelector('[data-sticky-quantity]');
      this.combinedSelect = this.querySelector('[data-sticky-variant-select]');
      this.button = this.querySelector('[data-sticky-add]');
      this.error = this.querySelector('[data-sticky-error]');
      const options = {signal: this.abort.signal};

      this.productContainer.addEventListener('variantChange', event => {
        this.variant = this.variants.get(String(event.detail.variant?.id));
        this.scheduleSync();
      }, options);
      this.productContainer.addEventListener('change', event => {
        if (event.target === this.variantSelect) {
          this.lastSelectValue = this.variantSelect.value;
          if (this.lastSelectValue) this.variant = this.resolveVariant(this.lastSelectValue);
        }
        this.scheduleSync();
      }, options);
      this.productContainer.addEventListener('input', this.scheduleSync, options);
      this.productContainer.addEventListener('click', this.scheduleSync, options);
      document.addEventListener('ajaxProduct:added', event => this.handleSuccessfulAdd(event), options);
      this.querySelector('[data-sticky-quantity-minus]').addEventListener('click', () => this.changeQuantity(-1), options);
      this.querySelector('[data-sticky-quantity-plus]').addEventListener('click', () => this.changeQuantity(1), options);
      this.quantity.addEventListener('change', () => this.writeQuantity(this.quantity.value), options);
      this.combinedSelect?.addEventListener('change', event => this.changeVariant(event.target.value), options);
      this.button.addEventListener('click', () => this.add(), options);
      window.addEventListener('scroll', this.scheduleSync, {passive: true, signal: this.abort.signal});
      window.addEventListener('resize', this.scheduleSync, options);
      window.addEventListener('pageshow', this.scheduleSync, options);
      document.addEventListener('shopify:section:load', () => {
        if (this.originalButton.isConnected) return;
        this.disconnectedCallback();
        this.connectedCallback();
      }, options);

      this.uiObserver = new MutationObserver(this.scheduleSync);
      this.uiObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});

      this.formObserver = new MutationObserver(this.scheduleSync);
      this.formObserver.observe(this.productContainer, {subtree: true, attributes: true, childList: true, characterData: true});
      if (window.IntersectionObserver) {
        this.visibilityObserver = new IntersectionObserver(this.scheduleSync);
        this.visibilityObserver.observe(this.originalButton);
      }
      if (document.fonts) document.fonts.ready.then(() => { if (this.isConnected) this.scheduleSync(); });
      this.sync();
    }

    changeVariant(id) {
      this.sync();
      const variant = this.variants.get(String(id));
      if (this.busy || !variant?.available) return;
      const inputs = Array.from(this.productContainer.querySelectorAll('[data-variant-input]'));
      const groups = variant.options.map((value, index) => inputs.filter(input => input.dataset.index === 'option' + (index + 1)));
      const matches = groups.map((group, index) => group.find(input => input.tagName === 'SELECT'
        ? Array.from(input.options).some(option => option.value === variant.options[index])
        : input.value === variant.options[index]));
      if (matches.every(Boolean)) {
        groups.forEach((group, index) => group.forEach(input => {
          if (input.type === 'radio' || input.type === 'checkbox') input.checked = input.value === variant.options[index];
          else input.value = variant.options[index];
        }));
        matches.forEach(input => {
          if (!input.dataset.colorName) return;
          const label = this.productContainer.querySelector('[data-variant-color-label][data-index="' + input.dataset.colorIndex + '"]');
          if (label) label.textContent = input.dataset.colorName;
        });
        matches[matches.length - 1].dispatchEvent(new Event('change', {bubbles: true}));
      } else {
        const json = this.productContainer.querySelector('[data-variant-json]');
        const nativeVariant = json && JSON.parse(json.textContent).find(item => String(item.id) === String(id));
        this.variantSelect.value = id;
        this.variantSelect.dispatchEvent(new Event('change', {bubbles: true}));
        if (nativeVariant) {
          const detail = {variant: nativeVariant, currentlySelectedValues: variant.options.map((value, index) => ({value, index: 'option' + (index + 1)}))};
          const events = ['variantChange'];
          const has = selector => Boolean(this.productContainer.querySelector(selector));
          if (['[data-product-price]', '[data-product-price-wrap]', '[data-compare-price]', '[data-save-price]',
            '[data-a11y-price]', '[data-compare-price-a11y]'].every(has)) events.push('variantPriceChange');
          if (nativeVariant.featured_media?.preview_image?.src && has('.product__thumb[data-id="' + nativeVariant.featured_media.id + '"]')) events.push('variantImageChange');
          if (has('[data-unit-price-wrapper]') && (!nativeVariant.unit_price ||
            (has('[data-unit-price]') && has('[data-unit-base]')))) events.push('variantUnitPriceChange');
          if (has('[data-sku]')) events.push('variantSKUChange');
          events.forEach(type =>
            this.productContainer.dispatchEvent(new CustomEvent(type, {detail})));
          document.dispatchEvent(new CustomEvent('variant:change', {detail}));
        }
      }
      this.sync();
    }

    syncVariantSelect() {
      if (!this.combinedSelect) return;
      const variant = this.variant;
      this.combinedSelect.parentElement.hidden = this.variants.size < 2 || Boolean(variant && !this.variants.has(String(variant.id)));
      this.combinedSelect.value = variant ? String(variant.id) : '';
      this.combinedSelect.disabled = this.busy;
      const style = getComputedStyle(this.combinedSelect);
      const context = document.createElement('canvas').getContext('2d');
      if (context) {
        context.font = style.font;
        const text = this.combinedSelect.selectedOptions[0]?.textContent || '';
        this.combinedSelect.style.width = Math.ceil(context.measureText(text).width) +
          parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + 2 + 'px';
      }
    }

    resolveVariant(id) {
      const selected = this.productContainer.querySelectorAll('[data-door-choice].is-selected, [data-ws-choice].is-selected, [data-windshield-option].is-selected');
      const choice = Array.from(selected).find(node => node.dataset.variantId === String(id));
      if (choice) {
        let comparePrice = choice.dataset.pdpComparePrice || '';
        if (choice.dataset.pdpComparePriceCents !== undefined &&
            !(Number(choice.dataset.pdpComparePriceCents) > Number(choice.dataset.pdpPriceCents))) comparePrice = '';
        return {id, available: choice.dataset.available === 'true',
          price: choice.dataset.pdpPrice || choice.dataset.price || '',
          priceCents: choice.dataset.pdpPriceCents !== undefined ? Number(choice.dataset.pdpPriceCents) : undefined,
          comparePrice, image: choice.dataset.image || ''};
      }
      return this.variants.get(String(id));
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    sync() {
      if (!this.originalButton?.isConnected) {
        this.hidden = true;
        return;
      }
      const rect = this.originalButton.getBoundingClientRect();
      this.hidden = !(rect.width > 0 && rect.height > 0 && rect.bottom <= 0);
      const style = getComputedStyle(this);
      const rendered = style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      const exposureKey = `product_sticky_add:${this.dataset.productId || location.pathname}`;
      const exposures = window.__KMProductStickyExposures || (window.__KMProductStickyExposures = new Set());
      if (!this.hidden && rendered && !this.exposureTracked && !exposures.has(exposureKey)) {
        this.exposureTracked = true;
        exposures.add(exposureKey);
        this.track('product_sticky_add_exposure', this.analyticsPayload());
      }

      const selectedValue = this.variantSelect?.value;
      if (selectedValue && selectedValue !== this.lastSelectValue) {
        this.variant = this.resolveVariant(selectedValue);
        this.lastSelectValue = selectedValue;
      }
      if (this.variant) this.variant = this.resolveVariant(this.variant.id) || this.variant;

      const variant = this.variant;
      this.busy = this.pendingAdd || this.originalButton.classList.contains('btn--loading') || this.originalButton.getAttribute('aria-busy') === 'true';
      this.button.disabled = this.busy || this.originalButton.disabled || !variant?.available;
      this.button.setAttribute('aria-busy', String(this.busy));
      this.syncVariantSelect();
      this.querySelector('[data-sticky-button-text]').textContent = !variant ? 'UNAVAILABLE' : (variant.available ? 'ADD TO CART' : 'SOLD OUT');
      this.querySelectorAll('[data-sticky-price]').forEach(node => { node.innerHTML = variant?.price || ''; });
      this.querySelectorAll('[data-sticky-compare]').forEach(node => {
        node.innerHTML = variant?.comparePrice || '';
        node.hidden = !variant?.comparePrice;
      });
      const image = this.querySelector('[data-sticky-image]');
      if (image && variant?.image && image.getAttribute('src') !== variant.image) image.src = variant.image;
      const title = this.productContainer.querySelector('.product-single__title')?.textContent;
      if (title) this.querySelector('[data-sticky-title]').textContent = title.trim();
      const quantity = this.readQuantity();
      if (document.activeElement !== this.quantity) this.quantity.value = quantity;
      this.querySelectorAll('[data-sticky-quantity-controls] button, [data-sticky-quantity]').forEach(node => { node.disabled = this.busy; });
      const error = this.busy ? '' : this.form.querySelector('.errors')?.textContent || '';
      this.error.textContent = error;
      this.error.hidden = !error;
    }

    readQuantity() {
      const min = Math.max(1, Number(this.originalQuantity.min) || 1);
      const value = Number(this.originalQuantity.value);
      return Number.isInteger(value) && value >= min ? value : min;
    }

    writeQuantity(value) {
      const min = Math.max(1, Number(this.originalQuantity.min) || 1);
      const max = Number(this.originalQuantity.max) || Infinity;
      const number = Number(value);
      this.originalQuantity.value = Math.min(max, Math.max(min, Number.isInteger(number) ? number : min));
      this.originalQuantity.dispatchEvent(new Event('change', {bubbles: true}));
      this.quantity.value = this.originalQuantity.value;
      this.sync();
    }

    changeQuantity(delta) {
      this.writeQuantity(this.readQuantity() + delta);
    }

    track(name, payload) {
      if (typeof window.KMTrackGaEvent === 'function') {
        window.KMTrackGaEvent(name, payload);
        return true;
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
        return true;
      }
      return false;
    }

    getProductTitle() {
      return this.dataset.productTitle || this.productContainer?.querySelector('.product-single__title')?.textContent?.trim() || '';
    }

    getPriceCents() {
      const price = this.variant?.priceCents;
      if (Number.isFinite(Number(price))) return Number(price);
      const formatted = String(this.variant?.price || '').replace(/[^0-9.,-]/g, '').replace(/,(?=\d{3}(?:\D|$))/g, '');
      const number = Number(formatted.replace(',', '.'));
      return Number.isFinite(number) ? Math.round(number * 100) : 0;
    }

    getCurrency() {
      return window.Shopify?.currency?.active || window.Shopify?.shop?.currency || document.documentElement.dataset.currency || 'USD';
    }

    analyticsPayload(quantity) {
      return {
        product_id: String(this.dataset.productId || ''),
        product_title: this.getProductTitle(),
        product_handle: this.dataset.productHandle || '',
        variant_id: String(this.variant?.id || ''),
        price: this.getPriceCents(),
        currency: this.getCurrency(),
        ...(quantity === undefined ? {} : {quantity: Number(quantity) || 1}),
        source: 'product_sticky_add'
      };
    }

    createAddId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `sticky-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    clearAttributionProperties() {
      this.attributionInputs?.forEach(input => input.remove());
      this.attributionInputs = [];
    }

    addAttributionProperties() {
      this.clearAttributionProperties();
      const values = {
        _km_sticky_add_source: 'product_page_sticky',
        _km_sticky_add_id: this.createAddId()
      };
      this.attributionInputs = Object.entries(values).map(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        this.form.append(input);
        return input;
      });
    }

    handleSuccessfulAdd(event) {
      if (!this.pendingAdd || event.detail?.addToCartBtn !== this.originalButton) return;
      const payload = this.pendingAddPayload || this.analyticsPayload(this.readQuantity());
      clearTimeout(this.addTimeout);
      this.clearAttributionProperties();
      this.pendingAdd = false;
      this.pendingAddPayload = null;
      this.track('product_sticky_add_to_cart', payload);
      this.sync();
    }

    add() {
      this.sync();
      if (this.button.disabled || this.pendingAdd) return;
      this.writeQuantity(this.quantity.value);
      this.addAttributionProperties();
      this.pendingAdd = true;
      this.pendingAddPayload = this.analyticsPayload(this.readQuantity());
      this.originalButton.click();
      this.sync();
      clearTimeout(this.addTimeout);
      this.addTimeout = setTimeout(() => {
        if (!this.pendingAdd) return;
        this.clearAttributionProperties();
        this.pendingAdd = false;
        this.pendingAddPayload = null;
        this.sync();
      }, 15000);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.formObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.uiObserver?.disconnect();
      clearTimeout(this.addTimeout);
      this.clearAttributionProperties();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  customElements.define('product-sticky-add', ProductStickyAdd);
})();
